import React, { useEffect, useState } from 'react';
import {
    Bar, BarChart, CartesianGrid, ComposedChart, Legend,
    Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { getBwaDashboard } from '../services/BwaService';
import '../styles/BwaAnalytics.css';

const MONTHS_SHORT = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtK(n) {
    if (n == null || isNaN(n)) return '';
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return (n / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 }) + ' Mio.';
    if (abs >= 1_000)     return (n / 1_000).toLocaleString('de-DE', { maximumFractionDigits: 1 }) + ' K';
    return n.toLocaleString('de-DE', { maximumFractionDigits: 0 });
}

function fmtEUR(n) {
    if (n == null || isNaN(n)) return '–';
    return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtPct(n) {
    if (n == null || isNaN(n)) return '–';
    return n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %';
}

function pctChange(curr, prev) {
    if (prev == null || prev === 0 || curr == null) return null;
    return ((curr - prev) / Math.abs(prev)) * 100;
}

function KpiCard({ label, value, margin, change, colorBySign }) {
    const positive = value >= 0;
    const valueColor = colorBySign
        ? positive ? 'var(--color-success, #16a34a)' : 'var(--color-danger, #dc2626)'
        : undefined;

    return (
        <div className="bwa-kpi-card">
            <div className="bwa-kpi-label">{label}</div>
            <div className="bwa-kpi-value" style={colorBySign ? { color: valueColor } : undefined}>
                {fmtEUR(value)}
            </div>
            <div className="bwa-kpi-meta">
                {margin != null && (
                    <span className="bwa-kpi-margin">{fmtPct(margin)} Marge</span>
                )}
                {change != null && (
                    <span className={`bwa-kpi-change ${change >= 0 ? 'up' : 'down'}`}>
                        {change >= 0 ? '▲' : '▼'} {fmtPct(Math.abs(change))} ggü. VJ
                    </span>
                )}
            </div>
        </div>
    );
}

function EurTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bwa-tooltip">
            <div className="bwa-tooltip-label">{label}</div>
            {payload.map((p, i) => (
                <div key={i} className="bwa-tooltip-row">
                    <span className="bwa-tooltip-dot" style={{ background: p.color }} />
                    <span>{p.name}</span>
                    <span className="bwa-tooltip-val">{fmtEUR(p.value)}</span>
                </div>
            ))}
        </div>
    );
}

export default function BwaAnalytics() {
    const [data,         setData]         = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [selectedYear, setSelectedYear] = useState(null);

    useEffect(() => {
        getBwaDashboard()
            .then(d => {
                setData(d);
                if (d.length) setSelectedYear(Math.max(...d.map(r => r.year)));
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="bwa-analytics-state">Lade…</div>;
    if (!data.length) return (
        <div className="bwa-analytics-state">
            <p>Noch keine BWA-Daten vorhanden.</p>
            <p>Bitte zuerst Daten in der Eingabe erfassen.</p>
        </div>
    );

    const years       = [...new Set(data.map(d => d.year))].sort((a, b) => a - b);
    const curData     = data.filter(d => d.year === selectedYear);
    const prevYear    = selectedYear - 1;
    const prevData    = data.filter(d => d.year === prevYear);
    const latest      = curData[curData.length - 1] ?? {};
    const latestPrev  = prevData.find(d => d.month === latest.month);

    const trendData = curData.map(d => ({
        name:         MONTHS_SHORT[d.month - 1],
        'Umsatz':     d.revenue        ?? 0,
        'Ergebnis':   d.prelim_result  ?? 0,
        'Betriebserg':d.op_result      ?? 0,
    }));

    const yoyData = MONTHS_SHORT
        .map((m, i) => {
            const month = i + 1;
            const c = curData.find(d => d.month === month);
            const p = prevData.find(d => d.month === month);
            if (!c && !p) return null;
            return {
                name: m,
                [`${selectedYear}`]: c?.revenue ?? null,
                [`${prevYear}`]:     p?.revenue ?? null,
            };
        })
        .filter(Boolean);

    const costData = curData.map(d => ({
        name:           MONTHS_SHORT[d.month - 1],
        'Personal':     d.personnel   ?? 0,
        'Raum':         d.rent        ?? 0,
        'Fahrzeug':     d.vehicles    ?? 0,
        'Marketing':    d.marketing   ?? 0,
        'Abschreibung': d.depreciation ?? 0,
        'Sonstige':     d.other_costs  ?? 0,
    }));

    let cumSum = 0;
    const cumData = curData.map(d => {
        cumSum += d.prelim_result ?? 0;
        return { name: MONTHS_SHORT[d.month - 1], 'Kum. Ergebnis': cumSum };
    });

    const rev    = latest.revenue;
    const gross  = latest.gross_profit;
    const opRes  = latest.op_result;
    const netRes = latest.prelim_result;

    const ytdRevenue = curData.reduce((s, d) => s + (d.revenue ?? 0), 0);
    const ytdResult  = curData.reduce((s, d) => s + (d.prelim_result ?? 0), 0);
    const prevYtdRev = prevData
        .filter(d => d.month <= (latest.month ?? 12))
        .reduce((s, d) => s + (d.revenue ?? 0), 0);

    return (
        <div className="bwa-analytics">

            <div className="bwa-analytics-toolbar">
                {years.map(y => (
                    <button
                        key={y}
                        className={`bwa-year-tab${selectedYear === y ? ' active' : ''}`}
                        onClick={() => setSelectedYear(y)}
                    >
                        {y}
                    </button>
                ))}
            </div>

            <div className="bwa-kpi-grid">
                <KpiCard
                    label={`Umsatz ${MONTHS_SHORT[(latest.month ?? 1) - 1]}`}
                    value={rev}
                    change={pctChange(rev, latestPrev?.revenue)}
                />
                <KpiCard
                    label="Rohertrag"
                    value={gross}
                    margin={rev ? (gross / rev) * 100 : null}
                />
                <KpiCard
                    label="Betriebsergebnis"
                    value={opRes}
                    margin={rev ? (opRes / rev) * 100 : null}
                    colorBySign
                />
                <KpiCard
                    label="Ergebnis (YTD)"
                    value={ytdResult}
                    change={pctChange(ytdRevenue, prevYtdRev)}
                    colorBySign
                />
            </div>

            <div className="bwa-chart-card">
                <h3 className="bwa-chart-title">Umsatz &amp; Ergebnis {selectedYear}</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={trendData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--color-text-muted)' }} />
                        <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} width={60} />
                        <Tooltip content={<EurTooltip />} wrapperStyle={{ background: 'none', border: 'none', boxShadow: 'none' }} />
                        <Legend />
                        <ReferenceLine y={0} stroke="var(--color-border)" />
                        <Bar     dataKey="Umsatz"      fill="#3b82f6" radius={[3, 3, 0, 0]} />
                        <Line   dataKey="Ergebnis"    stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} type="monotone" />
                        <Line   dataKey="Betriebserg" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 3" type="monotone" />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            <div className="bwa-charts-row">
                {prevData.length > 0 && (
                    <div className="bwa-chart-card">
                        <h3 className="bwa-chart-title">Umsatz: {selectedYear} vs. {prevYear}</h3>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={yoyData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--color-text-muted)' }} />
                                <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} width={60} />
                                <Tooltip content={<EurTooltip />} wrapperStyle={{ background: 'none', border: 'none', boxShadow: 'none' }} />
                                <Legend />
                                <Bar dataKey={`${selectedYear}`} fill="#3b82f6" radius={[3, 3, 0, 0]} />
                                <Bar dataKey={`${prevYear}`}     fill="#94a3b8" radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                <div className="bwa-chart-card">
                    <h3 className="bwa-chart-title">Kumuliertes Ergebnis {selectedYear}</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <ComposedChart data={cumData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--color-text-muted)' }} />
                            <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} width={60} />
                            <Tooltip content={<EurTooltip />} wrapperStyle={{ background: 'none', border: 'none', boxShadow: 'none' }} />
                            <ReferenceLine y={0} stroke="var(--color-border)" />
                            <Bar  dataKey="Kum. Ergebnis" fill={ytdResult >= 0 ? '#10b981' : '#ef4444'} radius={[3, 3, 0, 0]} />
                            <Line dataKey="Kum. Ergebnis" stroke={ytdResult >= 0 ? '#059669' : '#b91c1c'} strokeWidth={2} dot={false} type="monotone" />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {costData.some(d => Object.values(d).some((v, i) => i > 0 && v > 0)) && (
                <div className="bwa-chart-card">
                    <h3 className="bwa-chart-title">Kostenstruktur {selectedYear}</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={costData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--color-text-muted)' }} />
                            <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} width={60} />
                            <Tooltip content={<EurTooltip />} wrapperStyle={{ background: 'none', border: 'none', boxShadow: 'none' }} />
                            <Legend />
                            <Bar dataKey="Personal"     stackId="a" fill="#ef4444" />
                            <Bar dataKey="Raum"         stackId="a" fill="#f97316" />
                            <Bar dataKey="Fahrzeug"     stackId="a" fill="#eab308" />
                            <Bar dataKey="Marketing"    stackId="a" fill="#22c55e" />
                            <Bar dataKey="Abschreibung" stackId="a" fill="#6366f1" />
                            <Bar dataKey="Sonstige"     stackId="a" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            <div className="bwa-chart-card">
                <h3 className="bwa-chart-title">Monatsübersicht {selectedYear}</h3>
                <div className="bwa-summary-wrap">
                    <table className="bwa-summary-table">
                        <thead>
                            <tr>
                                <th>Monat</th>
                                <th className="num">Umsatz</th>
                                <th className="num">Rohertrag</th>
                                <th className="num">Marge</th>
                                <th className="num">Betriebserg.</th>
                                <th className="num">Ergebnis</th>
                                {prevData.length > 0 && <th className="num">Umsatz {prevYear}</th>}
                                {prevData.length > 0 && <th className="num">±</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {curData.map(d => {
                                const prev = prevData.find(p => p.month === d.month);
                                const chg  = pctChange(d.revenue, prev?.revenue);
                                return (
                                    <tr key={d.id}>
                                        <td>{MONTHS_SHORT[d.month - 1]}</td>
                                        <td className="num">{fmtEUR(d.revenue)}</td>
                                        <td className="num">{fmtEUR(d.gross_profit)}</td>
                                        <td className="num">{d.revenue ? fmtPct((d.gross_profit / d.revenue) * 100) : '–'}</td>
                                        <td className={`num ${(d.op_result ?? 0) < 0 ? 'neg' : ''}`}>{fmtEUR(d.op_result)}</td>
                                        <td className={`num ${(d.prelim_result ?? 0) < 0 ? 'neg' : ''}`}>{fmtEUR(d.prelim_result)}</td>
                                        {prevData.length > 0 && <td className="num muted">{fmtEUR(prev?.revenue)}</td>}
                                        {prevData.length > 0 && (
                                            <td className={`num ${chg == null ? '' : chg >= 0 ? 'pos' : 'neg'}`}>
                                                {chg != null ? (chg >= 0 ? '+' : '') + fmtPct(chg) : '–'}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td>Gesamt</td>
                                <td className="num">{fmtEUR(curData.reduce((s, d) => s + (d.revenue ?? 0), 0))}</td>
                                <td className="num">{fmtEUR(curData.reduce((s, d) => s + (d.gross_profit ?? 0), 0))}</td>
                                <td />
                                <td className={`num ${curData.reduce((s, d) => s + (d.op_result ?? 0), 0) < 0 ? 'neg' : ''}`}>
                                    {fmtEUR(curData.reduce((s, d) => s + (d.op_result ?? 0), 0))}
                                </td>
                                <td className={`num ${ytdResult < 0 ? 'neg' : ''}`}>{fmtEUR(ytdResult)}</td>
                                {prevData.length > 0 && (
                                    <td className="num muted">
                                        {fmtEUR(prevData.filter(d => d.month <= (latest.month ?? 12)).reduce((s, d) => s + (d.revenue ?? 0), 0))}
                                    </td>
                                )}
                                {prevData.length > 0 && (
                                    <td className={`num ${pctChange(ytdRevenue, prevYtdRev) >= 0 ? 'pos' : 'neg'}`}>
                                        {pctChange(ytdRevenue, prevYtdRev) != null
                                            ? (pctChange(ytdRevenue, prevYtdRev) >= 0 ? '+' : '') + fmtPct(pctChange(ytdRevenue, prevYtdRev))
                                            : '–'}
                                    </td>
                                )}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

        </div>
    );
}
